import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { Box, Card, Chip, CircularProgress, Typography } from '@mui/material';
import NoDataFound from '../../../components/NodataIndication/NoDataFound';
import ToolTip from '../../../components/ToolTip/ToolTip';
import { formatCurrency, getDeptColor, getDeptShortName } from '../utils/deptDisplay';
import { useElementSize } from '../utils/layoutHooks';

// Where this month's revenue came from, by department - the same look as the Refund Reasons Breakdown donut
// (views/refund/DashboardChart.jsx): a bordered card, centred title, a 65% donut with the total in the middle, white
// percentage labels on the slices, and a grid of rounded chips (dot, name, value) underneath.
// Each department keeps ITS colour (getDeptColor) so "Classroom is green" means the same here as on the cards, the
// table and the daily chart. A department with no revenue has no slice but still has its chip (₹ 0), like the zero
// reasons in the refund donut. Pure presentation of numbers already on the page.
//  - roomy (default): the 330 px donut with its legend chips (amount and share) underneath;
//  - dense: a 235 px donut with a share-only legend BESIDE it - used when the column beside the table is too short for
//    the roomy version (see useFitDensity), with thin slices left unlabelled (their share is in the legend);
//  - the card can be stretched by its parent (`sx`): the donut then grows into the height it is given, so the bottom of
//    the column lines up with the bottom of the table.
export default function DepartmentShareDonut({ departments, total, dailyAverage, loading, dense = false, sx }) {
  const legend = useMemo(() => [...departments].sort((a, b) => b.revenueToDate - a.revenueToDate), [departments]);
  const slices = useMemo(() => legend.filter((d) => d.revenueToDate > 0), [legend]);
  const sliceTotal = slices.reduce((sum, d) => sum + d.revenueToDate, 0);
  const percentOf = (amount) => (sliceTotal > 0 ? (amount / sliceTotal) * 100 : 0);

  const minChart = dense ? 235 : 330;
  const [chartBoxRef, chartBoxHeight, chartBoxWidth] = useElementSize();
  const chartHeight = Math.max(minChart, chartBoxHeight || 0);

  const options = useMemo(
    () => ({
      chart: { type: 'donut', fontFamily: 'inherit' },
      labels: slices.map((d) => getDeptShortName(d.deptId, d.deptName)),
      colors: slices.map((d) => getDeptColor(d.deptId)),
      legend: { show: false }, // the chips below are the legend
      stroke: { width: 1 },
      plotOptions: {
        pie: {
          // a thin slice has no room for a readable label at the compact size (they crowd each other); its share is in the legend
          ...(dense ? { dataLabels: { minAngleToShowLabel: 20 } } : {}),
          donut: {
            size: '65%',
            labels: {
              show: true,
              value: { show: true, fontSize: dense ? '16px' : '18px', fontWeight: 500, color: '#333', formatter: (v) => formatCurrency(v) },
              total: { show: true, label: 'Total Revenue', fontSize: dense ? '16px' : '20px', fontWeight: '600', color: '#333', formatter: () => formatCurrency(total) },
            },
          },
        },
      },
      tooltip: { y: { formatter: (v) => formatCurrency(v) } },
      responsive: [{ breakpoint: 768, options: { chart: { width: '100%' } } }],
    }),
    [slices, total, dense],
  );

  return (
    <Card sx={{ p: dense ? 2 : 3, borderRadius: '16px', border: '1px solid #d698cfff', display: 'flex', flexDirection: 'column', ...sx }}>
      <Typography variant={dense ? 'subtitle1' : 'h6'} fontWeight={600} textAlign="center" lineHeight={1.3}>
        Department Share (MTD)
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mb={dense ? 1 : 2}>
        Daily avg {formatCurrency(Math.round(dailyAverage || 0))}
      </Typography>

      {loading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={dense ? 240 : 300}>
          <CircularProgress />
        </Box>
      ) : slices.length === 0 ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={dense ? 160 : 200}>
          <NoDataFound message="No revenue recorded yet this month" />
        </Box>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: dense ? 'row' : 'column', flexWrap: dense ? 'wrap' : 'nowrap', gap: dense ? 1.5 : 2, alignItems: 'stretch' }}>
          {/* the donut takes whatever height is left over; it is drawn absolutely so it can never push the box taller */}
          <Box ref={chartBoxRef} sx={{ position: 'relative', flex: dense ? '1 1 250px' : '1 1 330px', minHeight: minChart, minWidth: 0 }}>
            <Box sx={{ position: 'absolute', inset: 0 }}>
              <Chart options={options} series={slices.map((d) => d.revenueToDate)} type="donut" height={chartHeight} width={chartBoxWidth || '100%'} />
            </Box>
          </Box>
          <Box sx={{ ...(dense ? { flex: '1 1 250px' } : { flex: '0 0 auto' }), display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${dense ? 150 : 290}px, 1fr))`, gap: dense ? 0.5 : 1, alignContent: 'center' }}>
            {legend.map((d) => (
              <Box key={d.deptId} sx={{ display: 'flex', alignItems: 'center', px: dense ? 1.25 : 1, py: dense ? 0.25 : 1, borderRadius: '12px', backgroundColor: '#f9f9f9', gap: 1 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, backgroundColor: getDeptColor(d.deptId) }} />
                <ToolTip
                  expand={dense ? `${d.deptName} · ${formatCurrency(d.revenueToDate)}` : d.deptName}
                  content={
                    <Typography sx={{ fontWeight: 500, fontSize: 14, maxWidth: dense ? 80 : 90, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {getDeptShortName(d.deptId, d.deptName)}
                    </Typography>
                  }
                />
                <Chip
                  sx={{ ml: 'auto' }}
                  size="small"
                  label={dense ? `${percentOf(d.revenueToDate).toFixed(1)}%` : `${formatCurrency(d.revenueToDate)} · ${percentOf(d.revenueToDate).toFixed(1)}%`}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Card>
  );
}
